import styled from "styled-components";

// Shared Win98 Explorer chrome (toolbar, address bar, web-view side panel,
// details list and status bar) used by My Computer and the Recycle Bin.

export const ExplorerContainer = styled.div<{
  $width?: number;
  $height?: number;
}>`
  width: ${(p) => p.$width ?? 680}px;
  height: ${(p) => p.$height ?? 420}px;
  max-width: calc(100vw - 16px);
  display: flex;
  flex-direction: column;
  background: ${(p) => p.theme.MSBackground};
  font-size: ${(p) => p.theme.fontNormal}px;
`;

export const Toolbar = styled.div`
  display: flex;
  align-items: stretch;
  gap: 2px;
  padding: 2px;
  border-top: 1px solid #fff;
  border-bottom: 1px solid #808080;
  flex-shrink: 0;
  overflow-x: auto;
`;

export const ToolbarDivider = styled.div`
  width: 2px;
  margin: 2px 3px;
  border-left: 1px solid #808080;
  border-right: 1px solid #fff;
`;

export const ToolbarButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-width: 56px;
  padding: 3px 6px;
  background: transparent;
  border: none;
  font-size: 11px;
  color: #000;
  cursor: pointer;
  white-space: nowrap;

  & svg,
  & img {
    width: 20px;
    height: 20px;
  }

  &:hover:not(:disabled) {
    box-shadow:
      -1px -1px 0 0 #808080 inset,
      1px 1px 0 0 #fff inset;
  }

  &:active:not(:disabled) {
    box-shadow:
      1px 1px 0 0 #808080 inset,
      -1px -1px 0 0 #fff inset;
  }

  /* classic embossed disabled text */
  &:disabled {
    color: #808080;
    text-shadow: 1px 1px 0 #fff;
    cursor: default;

    & svg,
    & img {
      filter: grayscale(1) opacity(0.45);
    }
  }
`;

export const AddressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 4px;
  border-top: 1px solid #fff;
  border-bottom: 1px solid #808080;
  flex-shrink: 0;
`;

export const AddressField = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 4px;
  height: 22px;
  padding: 0 4px;
  background: #fff;
  box-shadow: ${(p) => p.theme.invertedStandardBoxShadow};

  & img,
  & svg {
    width: 16px;
    height: 16px;
  }
`;

export const ExplorerBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  margin: 2px;
  background: #fff;
  box-shadow: ${(p) => p.theme.invertedStandardBoxShadow};
  padding: 2px;
`;

export const WebViewPanel = styled.div`
  width: 190px;
  flex-shrink: 0;
  padding: 14px 12px;
  overflow-y: auto;
  background: linear-gradient(180deg, #fff 0%, #fff 60%, #e8eefc 100%);
  line-height: 1.45;

  @media (max-width: 600px) {
    display: none;
  }
`;

export const WebViewIcon = styled.div`
  & img,
  & svg {
    width: 36px;
    height: 36px;
  }
`;

export const WebViewTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  margin: 6px 0 4px;
  line-height: 1.1;
  word-break: break-word;
`;

export const WebViewRule = styled.div`
  height: 3px;
  margin-bottom: 10px;
  background: linear-gradient(90deg, #00007b 0%, #1085d2 50%, #fff 100%);
`;

export const WebViewText = styled.div`
  font-size: 11px;
  color: #222;

  & + & {
    margin-top: 8px;
  }

  & b {
    font-weight: 700;
  }
`;

export const MainPane = styled.div`
  flex: 1;
  min-width: 0;
  overflow: auto;
  position: relative;
`;

export const IconGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: 12px;
  padding: 12px;
`;

export const GridItem = styled.button<{ $selected?: boolean }>`
  width: 84px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 4px 2px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 11px;
  color: #000;
  text-align: center;

  & img,
  & svg {
    width: 36px;
    height: 36px;
    ${(p) =>
      p.$selected &&
      "filter: brightness(0.75) sepia(1) hue-rotate(190deg) saturate(5);"}
  }

  & span {
    padding: 1px 2px;
    ${(p) =>
      p.$selected &&
      "background: #00007b; color: #fff; outline: 1px dotted #ffff00;"}
  }

  &:focus-visible {
    outline: none;
  }
`;

export const DetailsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
  table-layout: fixed;

  & th {
    position: sticky;
    top: 0;
    z-index: 1;
    text-align: left;
    font-weight: 400;
    padding: 2px 6px;
    background: ${(p) => p.theme.MSBackground};
    box-shadow: ${(p) => p.theme.standardBoxShadow};
    cursor: pointer;
    white-space: nowrap;
    user-select: none;

    &:active {
      box-shadow: ${(p) => p.theme.invertedStandardBoxShadow};
    }
  }

  & td {
    padding: 2px 6px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const DetailsRow = styled.tr<{ $selected?: boolean }>`
  cursor: default;

  & td:first-child span {
    ${(p) =>
      p.$selected
        ? "background: #00007b; color: #fff; outline: 1px dotted #ffff00;"
        : ""}
    padding: 0 2px;
  }

  & td:first-child {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  & td:first-child svg,
  & td:first-child img {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

export const StatusBar = styled.div`
  display: flex;
  gap: 2px;
  padding: 2px;
  flex-shrink: 0;
  font-size: 11px;
`;

export const StatusPane = styled.div<{ $grow?: boolean }>`
  ${(p) => (p.$grow ? "flex: 1;" : "min-width: 140px;")}
  padding: 2px 6px;
  box-shadow:
    1px 1px 0 0 #808080 inset,
    -1px -1px 0 0 #fff inset;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const CapacityBar = styled.div<{ $used: number }>`
  height: 12px;
  margin-top: 4px;
  background: #fff;
  box-shadow:
    1px 1px 0 0 #808080 inset,
    -1px -1px 0 0 #fff inset;
  padding: 2px;

  &::before {
    content: "";
    display: block;
    height: 100%;
    width: ${(p) => Math.min(100, Math.max(0, p.$used))}%;
    background: repeating-linear-gradient(
      90deg,
      #00007b 0 6px,
      transparent 6px 8px
    );
  }
`;
